import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useToast } from "@/components/ui/use-toast";
import { Search, Wrench, RefreshCw, AlertTriangle, CheckCircle } from "lucide-react";
import { supabase } from "@/lib/supabase";

export function UserDiagnostic() {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [diagnosticResult, setDiagnosticResult] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isRepairing, setIsRepairing] = useState(false);
  const { toast } = useToast();

  const diagnoseUser = async () => {
    if (!username.trim()) {
      toast({
        title: "Error",
        description: "Por favor ingresa un nombre de usuario",
        variant: "destructive"
      });
      return;
    }

    setIsLoading(true);
    setDiagnosticResult(null);

    try {
      const normalizedUsername = username.trim().toLowerCase();
      // Verificar en public.users
      const { data: publicUser, error: publicError } = await supabase
        .from('users')
        .select('*')
        .ilike('username', normalizedUsername)
        .single();

      // Verificar en auth.users usando la función Edge (priorizar búsqueda por id si existe publicUser)
      let authUser = null;
      try {
        const response = await fetch(`https://dundlwzoomxcqbulnylj.supabase.co/functions/v1/list-auth-users`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImR1bmRsd3pvb214Y3FidWxueWxqIiwicm9sZSI6InNlcnZpY2Vfcm9sZSIsImlhdCI6MTc0NDkzNTE0MCwiZXhwIjoyMDYwNTExMTQwfQ.pr7fabpKsjMmAHM03YnQojSMyJxjtlBFMxLs-qEWRyg`
          },
          body: JSON.stringify({ username: normalizedUsername, id: publicUser?.id })
        });

        if (response.ok) {
          const authData = await response.json();
          authUser = authData.user;
        }
      } catch (authError) {
        console.log('Error verificando auth.users:', authError);
      }

      const result = {
        username,
        publicUser: publicUser || null,
        authUser: authUser || null,
        status: getStatus(publicUser, authUser),
        issues: getIssues(publicUser, authUser)
      };

      setDiagnosticResult(result);
    } catch (error) {
      console.error('Error diagnosticando usuario:', error);
      toast({
        title: "Error",
        description: "No se pudo diagnosticar el usuario",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const repairUser = async () => {
    if (!username.trim() || !password.trim()) {
      toast({
        title: "Error",
        description: "Por favor ingresa usuario y contraseña",
        variant: "destructive"
      });
      return;
    }

    setIsRepairing(true);

    try {
      const normalizedUsername = username.trim().toLowerCase();
      // Primero intentar sincronizar contraseña (más específico para este problema)
      const syncResponse = await fetch(`https://dundlwzoomxcqbulnylj.supabase.co/functions/v1/sync-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImR1bmRsd3pvb214Y3FidWxueWxqIiwicm9sZSI6InNlcnZpY2Vfcm9sZSIsImlhdCI6MTc0NDkzNTE0MCwiZXhwIjoyMDYwNTExMTQwfQ.pr7fabpKsjMmAHM03YnQojSMyJxjtlBFMxLs-qEWRyg`
        },
        body: JSON.stringify({ username: normalizedUsername, password })
      });

      const syncData = await syncResponse.json();

      if (syncResponse.ok) {
        toast({
          title: "Contraseña sincronizada",
          description: syncData.message,
        });
        // Re-diagnosticar después de la sincronización
        await diagnoseUser();
        return;
      }

      // Si la sincronización falla, intentar reparación completa
      const repairResponse = await fetch(`https://dundlwzoomxcqbulnylj.supabase.co/functions/v1/repair-user`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImR1bmRsd3pvb214Y3FidWxueWxqIiwicm9sZSI6InNlcnZpY2Vfcm9sZSIsImlhdCI6MTc0NDkzNTE0MCwiZXhwIjoyMDYwNTExMTQwfQ.pr7fabpKsjMmAHM03YnQojSMyJxjtlBFMxLs-qEWRyg`
        },
        body: JSON.stringify({ username: normalizedUsername, password })
      });

      const repairData = await repairResponse.json();

      if (repairResponse.ok) {
        toast({
          title: "Usuario reparado",
          description: repairData.message,
        });
        // Re-diagnosticar después de la reparación
        await diagnoseUser();
      } else {
        toast({
          title: "Error",
          description: repairData.error || "No se pudo reparar el usuario",
          variant: "destructive"
        });
      }
    } catch (error) {
      console.error('Error reparando usuario:', error);
      toast({
        title: "Error",
        description: "No se pudo conectar con el servidor",
        variant: "destructive"
      });
    } finally {
      setIsRepairing(false);
    }
  };

  const updateAuthEmail = async () => {
    if (!diagnosticResult?.publicUser?.id || !diagnosticResult?.publicUser?.username) {
      toast({ title: "Error", description: "Primero diagnostica un usuario válido", variant: "destructive" });
      return;
    }

    setIsRepairing(true);
    try {
      const newEmail = diagnosticResult.publicUser.username.trim().toLowerCase();
      const response = await fetch(`https://dundlwzoomxcqbulnylj.supabase.co/functions/v1/update-user-email`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImR1bmRsd3pvb214Y3FidWxueWxqIiwicm9sZSI6InNlcnZpY2Vfcm9sZSIsImlhdCI6MTc0NDkzNTE0MCwiZXhwIjoyMDYwNTExMTQwfQ.pr7fabpKsjMmAHM03YnQojSMyJxjtlBFMxLs-qEWRyg`
        },
        body: JSON.stringify({ id: diagnosticResult.publicUser.id, newEmail })
      });

      const data = await response.json();
      if (response.ok) {
        toast({ title: "Email actualizado", description: data.message || "Email actualizado en Auth" });
        await diagnoseUser();
      } else if (response.status === 404) {
        toast({ title: "Función no encontrada", description: "Asegúrate de haber desplegado update-user-email en Supabase", variant: "destructive" });
      } else {
        toast({ title: "Error", description: data.error || "No se pudo actualizar el email en Auth", variant: "destructive" });
      }
    } catch (error) {
      console.error('Error actualizando email en Auth:', error);
      toast({ title: "Error", description: "No se pudo conectar con el servidor", variant: "destructive" });
    } finally {
      setIsRepairing(false);
    }
  };

  const getStatus = (publicUser, authUser) => {
    if (publicUser && authUser) {
      if (publicUser.id !== authUser.id) return "⚠️ IDs diferentes";
      if ((authUser.email ?? '').toLowerCase() !== (publicUser.username ?? '').toLowerCase()) return "⚠️ Email desincronizado";
      return "✅ Completo";
    } else if (publicUser && !authUser) {
      return "❌ Falta en auth.users";
    } else if (!publicUser && authUser) {
      return "❌ Falta en public.users";
    } else {
      return "❌ No existe en ninguna tabla";
    }
  };

  const getIssues = (publicUser, authUser) => {
    const issues = [];
    
    if (!publicUser) {
      issues.push("Usuario no existe en public.users");
    }
    
    if (!authUser) {
      issues.push("Usuario no existe en auth.users");
    }
    
    if (publicUser && authUser && publicUser.id !== authUser.id) {
      issues.push("IDs diferentes entre las tablas");
    }
    if (publicUser && authUser && (authUser.email ?? '').toLowerCase() !== (publicUser.username ?? '').toLowerCase()) {
      issues.push("Email en auth.users distinto de public.users");
    }
    
    if (authUser && !authUser.confirmed_at) {
      issues.push("Usuario no confirmado en auth.users");
    }
    
    return issues;
  };

  return (
    <div className="space-y-6">
      <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
        <h3 className="text-lg font-semibold mb-4 flex items-center">
          <Search className="mr-2 h-5 w-5" />
          Diagnóstico de Usuario
        </h3>
        
        <div className="grid gap-4 md:grid-cols-2">
          <div>
            <label className="block text-sm font-medium mb-1">Usuario</label>
            <Input
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder="usuario@ejemplo.com"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Contraseña (para reparación)</label>
            <Input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Contraseña actual"
            />
          </div>
        </div>
        
        <div className="flex gap-2 mt-4">
          <Button 
            onClick={diagnoseUser} 
            disabled={isLoading}
            className="flex items-center"
          >
            {isLoading ? (
              <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Search className="mr-2 h-4 w-4" />
            )}
            {isLoading ? "Diagnosticando..." : "Diagnosticar"}
          </Button>
          
          <Button 
            onClick={repairUser} 
            disabled={isRepairing || !diagnosticResult}
            variant="outline"
            className="flex items-center"
          >
            {isRepairing ? (
              <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Wrench className="mr-2 h-4 w-4" />
            )}
            {isRepairing ? "Reparando..." : "Reparar Usuario"}
          </Button>

          <Button 
            onClick={updateAuthEmail} 
            disabled={isRepairing || !diagnosticResult}
            variant="secondary"
            className="flex items-center"
          >
            <Wrench className="mr-2 h-4 w-4" />
            Actualizar email en Auth
          </Button>
        </div>
      </div>

      {diagnosticResult && (
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <h3 className="text-lg font-semibold mb-4">Resultado del Diagnóstico</h3>
          
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <span className="font-medium">Usuario:</span>
              <span>{diagnosticResult.username}</span>
            </div>
            
            <div className="flex items-center gap-2">
              <span className="font-medium">Estado:</span>
              <span className={`flex items-center gap-1 ${
                diagnosticResult.status.includes('✅') ? 'text-green-600' :
                diagnosticResult.status.includes('⚠️') ? 'text-yellow-600' :
                'text-red-600'
              }`}>
                {diagnosticResult.status.includes('✅') && <CheckCircle className="h-4 w-4" />}
                {diagnosticResult.status.includes('⚠️') && <AlertTriangle className="h-4 w-4" />}
                {diagnosticResult.status.includes('❌') && <AlertTriangle className="h-4 w-4" />}
                {diagnosticResult.status}
              </span>
            </div>

            {diagnosticResult.publicUser && (
              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="font-medium mb-2">Datos en public.users:</h4>
                <div className="text-sm space-y-1">
                  <div><span className="font-medium">ID:</span> {diagnosticResult.publicUser.id}</div>
                  <div><span className="font-medium">Usuario (email):</span> {diagnosticResult.publicUser.username}</div>
                  <div><span className="font-medium">Nombre:</span> {diagnosticResult.publicUser.name}</div>
                  <div><span className="font-medium">Rol:</span> {diagnosticResult.publicUser.role}</div>
                  <div><span className="font-medium">Contraseña:</span> {diagnosticResult.publicUser.password}</div>
                </div>
              </div>
            )}

            {diagnosticResult.authUser && (
              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="font-medium mb-2">Datos en auth.users:</h4>
                <div className="text-sm space-y-1">
                  <div><span className="font-medium">ID:</span> {diagnosticResult.authUser.id}</div>
                  <div><span className="font-medium">Email:</span> {diagnosticResult.authUser.email}</div>
                  <div><span className="font-medium">Confirmado:</span> {diagnosticResult.authUser.confirmed_at ? 'Sí' : 'No'}</div>
                  <div><span className="font-medium">Último login:</span> {diagnosticResult.authUser.last_sign_in_at || 'Nunca'}</div>
                </div>
              </div>
            )}

            {diagnosticResult.issues.length > 0 && (
              <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                <h4 className="font-medium text-red-800 mb-2">Problemas detectados:</h4>
                <ul className="text-sm text-red-700 space-y-1">
                  {diagnosticResult.issues.map((issue, index) => (
                    <li key={index} className="flex items-center gap-2">
                      <AlertTriangle className="h-4 w-4" />
                      {issue}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
} 